import React, { useEffect, useMemo, useRef, useState } from "react";
import { Accordion, Alert, Button, Card, Col, Form, Row, Tab, Table, Tabs } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useRevalidator, useRouteLoaderData } from "react-router-dom";

import { useAppApi } from "@/App";
import { GameFeatureFlags, GamePublicationState, GameStreamMode, GameType, VK_LIVE_ID_VKPLAYSTORE } from "@/const";
import { useRequest } from "@components/AppHook";
import { DevForm, FormFieldErrorMessage, FormFieldGroup, FormFieldHelpText } from "@components/Forms/Fields";
import { fieldChange, useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import {
    GameNameField,
    GameSlugField,
    IframeLandingUrlsField,
    LPIframeLandingUrlsField,
    LocalizersDataField,
} from "@components/Game/Fields";
import { GameToBrowserButton, GameToGameCenterButton } from "@components/Game/Misc";
import { Awesome, DevModal, LocaleDropdown } from "@components/Helpers";
import { CommonCard } from "@components/Pages";
import { ScrollToElement, arrayMove, isEmptyObject, url_reverse } from "@components/Utils";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Countries from "@screens/Game/Countries";
import Highlights from "@screens/Game/Highlights";

export default function Admin({ gameId, locl, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData, apiIsLoading, setApiReget } = useAppApi(
        url_reverse("api:game:admin", { game_id: gameId })
    );
    const onFormSubmit = useSubmit();
    const { api_post } = useRequest();
    const [curLoc, setCurLoc] = useState(locl);
    const [isCasual, setIsCasual] = useState(false);
    const [isBrowser, setIsBrowser] = useState(false);
    const [isAuto, setIsAuto] = useState(false);
    const [isManual, setIsManual] = useState(false);
    const [isCombo, setIsCombo] = useState(false);
    const revalidator = useRevalidator();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const commonInfo = useRouteLoaderData("commonInfo");
    const [showModal, setShowModal] = useState(false);
    const submitRef = useRef(null);
    const props = { gameId, curLoc, header, apiData, setApiData, apiIsLoading };
    const location = useLocation();
    const [activeKey, setActiveKey] = useState("main");

    // Список измененных полей для передачи в модальное окно
    const [changedImportantFields, setChangedImportantFields] = useState([]);

    // Поля, об изменении которых нужно сообщить в модальном окне
    const importantFields = [
        "cont_url",
        "main_page_url",
        "vkplay_sub_page_url",
        "main_page_gc_mt_link_id",
        "sub_page_gc_mt_link_id",
        "my_tracker_project_id",
        "my_tracker_app_id",
        "moimir_page_url",
        "vk_downloadable_page_url",
        "developer",
        "type",
    ];

    const isM2mGameDevsValid = (data, initial) => {
        if (initial !== "" && initial.length === data.length) {
            return true;
        }
        if (!commonInfo.account.is_super) {
            return true;
        }
        return data.length <= 5;
    };

    const isM2mPublishersValid = (data, initial) => {
        if (initial !== "" && initial.length === data.length) {
            return true;
        }
        if (!commonInfo.account.is_super) {
            return true;
        }
        return data.length <= 5;
    };

    const importantWithValidationFields = [
        ["m2m_gamedevs", isM2mGameDevsValid],
        ["m2m_publishers", isM2mPublishersValid],
    ];

    const mainform = apiData?.forms_data?.main.data;
    const mainformFields = apiData?.forms_data?.main.fields;
    const mainformErrors = apiData?.forms_data?.main.errors;

    useEffect(() => {
        if (mainform) {
            const newApiData = { ...apiData };

            newApiData.forms_data.main.fields.gc_registration_webform_url.disabled = false;
            newApiData.forms_data.main.fields.gc_autoreg.disabled = false;

            if (mainform.gc_accept_login === 0) {
                newApiData.forms_data.main.fields.gc_autoreg.disabled = true;
                newApiData.forms_data.main.fields.gc_registration_webform_url.disabled = true;
            } else if (mainform.gc_autoreg === true) {
                newApiData.forms_data.main.fields.gc_registration_webform_url.disabled = true;
            }

            newApiData.forms_data.main.fields.gc_installed_name.disabled = mainform.gc_installed_name_auto;

            setApiData(newApiData);
            setIsCasual(mainform.feature_flags?.includes(GameFeatureFlags.CASUAL));
            setIsBrowser(mainform.feature_flags?.includes(GameFeatureFlags.BROWSER));
            setIsAuto(mainform.stream_mode === GameStreamMode.AUTO);
            setIsManual(mainform.stream_mode === GameStreamMode.MANUAL);
            setIsCombo(mainform.stream_mode === GameStreamMode.COMBO);

            if (mainform.vk_live_id.trim().length > 0 && mainform.stream_mode === "") {
                mainform.stream_mode = GameStreamMode.AUTO;
            }

            if (mainform.vk_live_id.trim().length === 0 && mainform.stream_mode !== "") {
                mainform.stream_mode = "";
            }
        }
    }, [JSON.stringify(mainform)]);

    useEffect(() => {
        const newApiData = { ...apiData };
        if (newApiData.forms_data) {
            if (newApiData.forms_data.main.data.type === GameType.CODE) {
                newApiData.forms_data.main.fields.multiple_buy.disabled = false;
            } else {
                newApiData.forms_data.main.fields.multiple_buy.disabled = true;
            }
            setApiData(newApiData);
        }
    }, [JSON.stringify(mainform)]);

    useEffect(() => {
        if (!location.hash) return;

        const hash = location.hash.slice(1);
        if (hash === "highlights") {
            setActiveKey("hl");
        } else {
            // скролл до нужного поля при переходе из чек-листа публикации
            setTimeout(() => {
                const element =
                    document.querySelector(`[name$="-${hash}"]`) || document.querySelector(`[class$="-${hash}-anchr"]`);
                if (element) {
                    const targetElement = element.closest(".form-group") || element;
                    ScrollToElement(targetElement);
                    window.history.replaceState(null, "", window.location.pathname + window.location.search);
                }
            }, 1000);
        }
    }, [location]);

    if (!apiData) return;

    const changeVkLiveId = (vkLiveProps) => {
        fieldChange(vkLiveProps);
        if (mainform.vk_live_id !== "") {
            mainform.stream_mode =
                mainform.vk_live_id === VK_LIVE_ID_VKPLAYSTORE ? GameStreamMode.MANUAL : GameStreamMode.AUTO;
        } else if (mainform.stream_mode !== "") {
            mainform.stream_mode = "";
        }
    };

    const doAction = (ev) => {
        api_post(ev, url_reverse("api:game:admin_actions", { game_id: gameId }), {
            params: { action: ev.target.name },
            then: (resp) => {
                if (resp.status === "ok") {
                    // релоадим страницу, чтобы не было ее устаревания (либо надо как то переделывать все)
                    window.location.reload();
                }
            },
        });
    };

    const updateCatalogdGame = (ev) => {
        ev.target.disabled = true;
        api_post(ev, url_reverse("api:game:admin_actions", { game_id: gameId }), {
            params: { action: "update_catalogd_game" },
            then: (resp) => {
                if (resp.status === "ok") {
                    const msg = "resp: " + resp.message;
                    alert(msg);
                    ev.target.disabled = false;
                }
            },
        });
    };

    const headerTools = (
        <>
            <LocaleDropdown
                curLoc={curLoc}
                setCurLoc={setCurLoc}
                active_locales={apiData.active_locales}
                base_locale={mainform?.base_locale}
                forms_data={apiData.forms_data}
            />{" "}
            <Link
                className="btn btn-sm btn-info"
                to={url_reverse(
                    "admin:actionlog:list",
                    { kind: "default" },
                    { "filter-entity_label": mainform?.gc_id }
                )}
                target="_blank"
            >
                <Awesome type="wheelchair" />
            </Link>{" "}
            <GameToBrowserButton game={{ ...gameCommonInfo.game, parent: gameCommonInfo.parent }} asButton={true} />{" "}
            <GameToGameCenterButton game={{ ...gameCommonInfo.game, parent: gameCommonInfo.parent }} asButton={true} />{" "}
            <Button
                className="btn btn-sm btn-info"
                name="update_catalogd_game"
                onClick={(ev) => {
                    updateCatalogdGame(ev);
                }}
            >
                <Awesome type="refresh" tooltip={"Обновить данные в catalogd"} />
            </Button>
        </>
    );

    const fieldProps = { apiData, setApiData, apiIsLoading };

    const markerFieldData = mainformFields?.marking.widget.attrs?.additional_information;
    const markerData = (marker) => {
        return markerFieldData.find((item) => item.slug === marker.value);
    };

    const updateMarking = (ev) => {
        const newApiData = { ...apiData };
        const currenVal = ev.val;
        if (Array.isArray(currenVal)) {
            const isNotMarking = currenVal.indexOf("no_marking") === 0;
            const isNotRequired = currenVal.indexOf("no_required") === 0;

            if (isNotMarking && currenVal.length > 1) {
                mainform.marking = currenVal.filter((item) => item !== "no_marking");
            } else if (isNotRequired && currenVal.length > 1) {
                mainform.marking = currenVal.filter((item) => item !== "no_required");
            } else if (currenVal.includes("no_required")) {
                mainform.marking = ["no_required"];
            } else if (currenVal.includes("no_marking")) {
                mainform.marking = ["no_marking"];
            } else {
                mainform.marking = currenVal;
            }
            setApiData(newApiData);
        }
    };

    const markerImage = (option, { context }) => (
        <>
            {markerFieldData && (
                <div className="d-flex align-items-center justify-content-between">
                    <div className="mr-2">
                        <img src={markerData(option)?.icon} style={{ maxWidth: 50 }} />
                    </div>
                    <div className="flex-grow-1">
                        {context === "value" ? option.label : markerData(option).description}
                    </div>
                </div>
            )}
        </>
    );

    function errorFunction(name, locale) {
        if (locale) {
            setCurLoc(locale);
        }
    }

    const submitFormHandle = (ev) => {
        const urls = apiData.lpifrurls.map((o) => o.url);
        api_post(ev, url_reverse("api:game:save_lpifrurls", { game_id: gameCommonInfo.game.id }), {
            params: { urls: encodeURIComponent(urls) },
            then: (resp) => {
                if (resp.status === "ok") {
                    onFormSubmit(ev, {
                        onOK: (resp2) => {
                            setApiData(resp2);
                            revalidator.revalidate();
                        },
                    });
                }
            },
        });
    };

    const checkImportantFields = (ev) => {
        const result = {};
        importantFields.forEach((field) => {
            const initial = mainformFields[field]?.initial || "";
            const data = mainform[field] || "";
            if (String(data) !== String(initial)) {
                result[field] = mainformFields[field];
            }
        });

        importantWithValidationFields.forEach(([field, isFieldValid]) => {
            const data = mainform[field] || [];
            const initial = mainformFields[field]?.initial || "";
            if (!isFieldValid(data, initial)) {
                result[field] = mainformFields[field];
            }
        });

        if (isEmptyObject(result)) {
            submitFormHandle(ev);
        } else {
            setChangedImportantFields(result);
            setShowModal(true);
            // Система костылей для отправки формы
            submitRef.current = () => submitFormHandle(ev);
        }
    };

    const beforeSubmitHandle = (ev) => {
        ev.preventDefault();
        checkImportantFields(ev);
    };

    const handleCloudGame = (e) => {
        fieldChange(e);
        if (e.val === false && mainform.only_cloud_available === true) {
            mainform.only_cloud_available = false;
        }
    };

    const handleOnlyCloudAvailable = (e) => {
        fieldChange(e);
        if (e.val === true && mainform.cloud_game === false) {
            mainform.cloud_game = true;
        }
    };

    // Удаление старых якорей при переходе между вкладками
    const handleSelect = () => {
        const correctUrl = url_reverse("games:admin", { game_id: gameId });
        window.history.pushState({}, "", correctUrl);
    };
    const showFetchParentLocaleButton = !gameCommonInfo.game.is_standalone_game && !apiData.active_locales[curLoc];
    const fetchParentLocale = (ev) => {
        api_post(ev, url_reverse("api:game:misc_actions", { game_id: gameId }), {
            params: { action: ev.target.name, locale: ev.target.value },
            then: () => {
                setApiReget(true);
            },
        });
    };

    return (
        <CommonCard header={header} headerTools={headerTools} errorFunction={errorFunction}>
            {showFetchParentLocaleButton ? (
                <DevForm.Button
                    variant="btn btn-primary"
                    name="fetch_parent_locale"
                    value={curLoc}
                    onClick={fetchParentLocale}
                >
                    {t("Добавить локализацию")}
                </DevForm.Button>
            ) : (
                <>
                    {apiData?.same_names && (
                        <Alert variant="warning">
                            {t("Существуют игры с таким же названием")}
                            <ul>
                                {apiData.same_names.map((item) => (
                                    <li key={`same-${item.id}`}>
                                        <a href={url_reverse("games:admin", { game_id: item.id })}>{item.name}</a> (
                                        {item.locale})
                                    </li>
                                ))}
                            </ul>
                        </Alert>
                    )}
                    <ChangedFieldsModalAlert
                        fields={changedImportantFields}
                        showModal={showModal}
                        handleClose={() => setShowModal(false)}
                        submitForm={submitRef?.current}
                    />
                    <Form onSubmit={beforeSubmitHandle}>
                        <Tabs
                            defaultActiveKey={activeKey}
                            transition={false}
                            className="react-bootstrap-tabs-fix"
                            onSelect={handleSelect}
                        >
                            <Tab eventKey="main" title={t("Основное")}>
                                <DevForm.Field {...fieldProps} prefix="main" name="date_modified" />
                                <GameNameField {...fieldProps} prefix={curLoc} />
                                <DevForm.Field {...fieldProps} prefix="main" name="type" />
                                <DevForm.Field {...fieldProps} prefix="main" name="technical_name" />
                                <DevForm.Field {...fieldProps} prefix="main" name="alter_names" />
                                <GameSlugField {...fieldProps} helpText={mainform.my_tracker_app_id ? null : " "} />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="descr" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="header_long_descr" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="long_descr" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="about_text" />
                                <DevForm.Field {...fieldProps} prefix="main" name="main_genre" />
                                <DevForm.Field {...fieldProps} prefix="main" name="genre" />
                                <DevForm.Field {...fieldProps} prefix="main" name="tags" />
                                <DevForm.Field {...fieldProps} prefix="main" name="labels" />
                                <DevForm.Field {...fieldProps} prefix="main" name="territory_activation" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="picture" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="picture_horizontal" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="picture_vertical" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="picture_bundle" />
                                <DevForm.Field {...fieldProps} prefix="main" name="video" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="icon" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="art" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="wallpaper" />
                                <DevForm.Field {...fieldProps} prefix="main" name="wallpaper_gradient" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="logo_cover_astrum" />
                                <DevForm.Field {...fieldProps} prefix="main" name="logo" />
                                <DevForm.Field {...fieldProps} prefix="main" name="cont_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="main_page_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="vkplay_sub_page_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="main_page_gc_mt_link_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="sub_page_gc_mt_link_id" />

                                <Row className="mb-2">
                                    <Col sm={3} />
                                    <Col sm={9}>
                                        <DevForm.Button
                                            variant="secondary"
                                            name="generate_mytracker_links"
                                            onClick={doAction}
                                        >
                                            {t("Сгенерировать ссылки MyTracker")}
                                        </DevForm.Button>
                                    </Col>
                                </Row>

                                <DevForm.Field {...fieldProps} prefix="main" name="moimir_page_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="rustore_page_url" />
                                {!isBrowser && !isCasual && (
                                    <>
                                        <DevForm.Field {...fieldProps} prefix="main" name="vk_downloadable_page_url" />
                                    </>
                                )}
                                <DevForm.Field {...fieldProps} prefix="main" name="direct_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="promo_flags" />
                                <DevForm.Field {...fieldProps} prefix="main" name="feature_flags" />
                                <DevForm.Field {...fieldProps} prefix="main" name="special_flags" />
                                <DevForm.Field
                                    {...fieldProps}
                                    prefix="main"
                                    name="marking"
                                    formatOptionLabel={markerImage}
                                    onChange={(ev) => {
                                        updateMarking(ev);
                                    }}
                                />
                                <DevForm.Field {...fieldProps} prefix="main" name="show_info_about_in_development" />
                                <DevForm.Field {...fieldProps} prefix="main" name="show_info_about_early_access" />
                                <DevForm.Field {...fieldProps} prefix="main" name="age_limit" />
                                <DevForm.Field {...fieldProps} prefix="main" name="is_steam_gift" />
                                <DevForm.Field {...fieldProps} prefix="main" name="is_laboratory" />
                                <DevForm.Field
                                    {...fieldProps}
                                    prefix="main"
                                    name="cloud_game"
                                    onChange={handleCloudGame}
                                />
                                <DevForm.Field
                                    {...fieldProps}
                                    prefix="main"
                                    name="only_cloud_available"
                                    onChange={handleOnlyCloudAvailable}
                                />
                                <DevForm.Field {...fieldProps} prefix="main" name="is_non_commercial" />
                                <DevForm.Field {...fieldProps} prefix="main" name="is_control_in_box" />
                                <DevForm.Field {...fieldProps} prefix="main" name="offline_mode" />
                                <DevForm.Field {...fieldProps} prefix="main" name="extra_flags" />
                                <DevForm.Field {...fieldProps} prefix="main" name="developer" />
                                <DevForm.Field {...fieldProps} prefix="main" name="m2m_gamedevs" />
                                <DevForm.Field {...fieldProps} prefix="main" name="m2m_publishers" />
                                <DevForm.Field {...fieldProps} prefix="main" name="content_source" />
                                <DevForm.Field {...fieldProps} prefix="main" name="platform" />
                                <DevForm.Field {...fieldProps} prefix="main" name="has_extra_codes" />
                                <DevForm.Field {...fieldProps} prefix="main" name="auth_system" />
                                <DevForm.Field {...fieldProps} prefix="main" name="sezam_migration" />
                                <DevForm.Field {...fieldProps} prefix="main" name="password_migration" />
                                <DevForm.Field {...fieldProps} prefix="main" name="entry_points" />
                                <DevForm.Field {...fieldProps} prefix="main" name="project_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="project_secret" />
                                <DevForm.Field {...fieldProps} prefix="main" name="sezam_client_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="sezam_client_secret" />
                                <DevForm.Field {...fieldProps} prefix="main" name="sezam_project_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="pc_game_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="my_tracker_project_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="my_tracker_app_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="ok_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="ext_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="steam_app_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="steam_package_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="starforce_workspace_id" />
                                <DevForm.Field
                                    {...fieldProps}
                                    name="vk_live_id"
                                    prefix="main"
                                    onChange={(vkLiveProps) => changeVkLiveId(vkLiveProps)}
                                />
                                <FormFieldGroup
                                    label={mainformFields?.stream_mode.label}
                                    required={mainformFields?.stream_mode.required}
                                >
                                    <DevForm.Field
                                        {...fieldProps}
                                        name="stream_mode"
                                        prefix="main"
                                        bareControl={true}
                                    />
                                    <FormFieldErrorMessage error={mainformErrors?.stream_mode} custom={true} />
                                    {mainform?.stream_mode === "" && (
                                        <FormFieldHelpText help_text={mainformFields?.stream_mode.help_text} />
                                    )}
                                    {mainform?.stream_mode === "auto" && (
                                        <FormFieldHelpText
                                            help_text={t(
                                                "Автоматический режим — трансляции любого стримера VK Play Live по игре будут отображаться на странице игры."
                                            )}
                                        />
                                    )}
                                    {mainform?.stream_mode === "manual" && (
                                        <FormFieldHelpText
                                            help_text={t(
                                                "Ручной режим — отображаются только трансляции стримеров, добавленных в настройках вручную."
                                            )}
                                        />
                                    )}
                                    {mainform?.stream_mode === "combo" && (
                                        <FormFieldHelpText
                                            help_text={t(
                                                "Комбинированный режим — отображаются трансляции по условиям, заданным в настройках."
                                            )}
                                        />
                                    )}
                                </FormFieldGroup>
                                {(isAuto || isCombo) && (
                                    <>
                                        <DevForm.Field {...fieldProps} prefix="main" name="stream_count" />
                                        <DevForm.Field {...fieldProps} prefix="main" name="stream_min_viewers" />
                                    </>
                                )}
                                {(isManual || isCombo) && (
                                    <>
                                        <DevForm.Field
                                            {...fieldProps}
                                            prefix="main"
                                            name="streamers_to_show"
                                            prepend="https://vkplay.live/"
                                        />
                                        <DevForm.Field
                                            {...fieldProps}
                                            prefix="main"
                                            name="streamers_to_hide"
                                            prepend="https://vkplay.live/"
                                        />
                                    </>
                                )}
                                {(isManual || isCombo || isAuto) && (
                                    <Accordion className="mb-3">
                                        <Accordion.Toggle as={Card.Header} eventKey="main" variant="link">
                                            <strong className="mr-2">{t("Настройка показа блока трансляции")}</strong>
                                            <Awesome type="angle-down" />
                                        </Accordion.Toggle>
                                        <Accordion.Collapse eventKey="main">
                                            <Card.Body>
                                                <DevForm.Field {...fieldProps} prefix="main" name="show_stream_from" />
                                                <DevForm.Field {...fieldProps} prefix="main" name="show_stream_till" />
                                            </Card.Body>
                                        </Accordion.Collapse>
                                    </Accordion>
                                )}
                                <DevForm.Field {...fieldProps} prefix="main" name="stream_gc_expanded" />
                                <DevForm.Field {...fieldProps} prefix="main" name="stream_web_only" />
                                <DevForm.Field {...fieldProps} prefix="main" name="api_secret" />
                                <IframeLandingUrlsField {...fieldProps} />
                                <LPIframeLandingUrlsField {...fieldProps} />
                                <DevForm.Field {...fieldProps} prefix="main" name="iframe_background_color" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="support_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="oauth_redirect_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="preorder_banner" />
                                <DevForm.Field {...fieldProps} prefix="main" name="desktop_icon" />
                                <DevForm.Field {...fieldProps} prefix="main" name="date_published" />
                                <DevForm.Field {...fieldProps} prefix="main" name="date_launch" />
                                <DevForm.Field {...fieldProps} prefix="main" name="date_release_estimated" />
                                <DevForm.Field {...fieldProps} prefix="main" name="date_release_estimated_variant" />
                                <DevForm.Field {...fieldProps} prefix="main" name="date_release_global" />
                                <DevForm.Field {...fieldProps} prefix="main" name="date_launch_wishlist" />
                                <DevForm.Field {...fieldProps} prefix="main" name="date_release_notify" />
                                <DevForm.Field {...fieldProps} prefix="main" name="date_start_sale" />
                                <DevForm.Field {...fieldProps} prefix="main" name="date_start_download" />
                                <DevForm.Field {...fieldProps} prefix="main" name="date_advanced_access" />
                                {mainform.type === GameType.CODE && (
                                    <>
                                        <DevForm.Field {...fieldProps} prefix="main" name="key_source" />
                                        <DevForm.Field {...fieldProps} prefix="main" name="multiple_buy" />
                                    </>
                                )}

                                <hr />
                                <DevForm.Field {...fieldProps} prefix="main" name="tax_type" />
                                <DevForm.Field {...fieldProps} prefix="main" name="payments_stopped" />
                                <DevForm.Field {...fieldProps} prefix="main" name="merchant_set" />
                                {gameCommonInfo.game.is_sellable && (
                                    <>
                                        <DevForm.Field {...fieldProps} prefix="main" name="temp_merchant_set" />
                                        <DevForm.Field {...fieldProps} prefix="main" name="temp_merchant_date_start" />
                                        <DevForm.Field {...fieldProps} prefix="main" name="temp_merchant_date_finish" />
                                    </>
                                )}
                                <hr />

                                <DevForm.Field {...fieldProps} prefix="main" name="payment_date_start" />
                                <DevForm.Field {...fieldProps} prefix="main" name="payment_date_finish" />
                                <DevForm.Field {...fieldProps} prefix="main" name="payment_callback_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="refund_callback_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="partner_redirect_url" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="platforms" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="game_modes" />
                                <DevForm.Field {...fieldProps} prefix="main" name="localizers" />
                                <LocalizersDataField
                                    {...fieldProps}
                                    prefix="main"
                                    name="localizers_data"
                                    from_admin_page={true}
                                />
                                <DevForm.Field {...fieldProps} prefix="main" name="copyright" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="project_terms" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="gc_waitplay" />
                                <DevForm.Field {...fieldProps} prefix="main" name="base_locale" />
                                <DevForm.Field {...fieldProps} prefix="main" name="supported_os" />
                                <DevForm.Field {...fieldProps} prefix="main" name="ext_partner" />
                                <DevForm.Field {...fieldProps} prefix="main" name="request_basic_promo" />
                                <DevForm.Field {...fieldProps} prefix="main" name="flag_beta" />
                                {gameCommonInfo.game.manual_publication_data &&
                                mainform?.publication_state === GamePublicationState.TESTING_MARKETING ? (
                                    <Alert variant="danger" className="mt-2 mb-5">
                                        <Trans>
                                            Установка флагов публикации игры в режиме отложенного запуска производится в
                                            разделе "Публикация"
                                        </Trans>
                                    </Alert>
                                ) : (
                                    <DevForm.Field {...fieldProps} prefix="main" name="visibility_state" />
                                )}

                                <DevForm.Button variant="primary" type="submit" name="save">
                                    {t("Сохранить")}
                                </DevForm.Button>

                                <div className="float-right">
                                    {!mainform?.project_id && (
                                        <>
                                            <DevForm.Button variant="secondary" name="create_souz" onClick={doAction}>
                                                {t("Создать ID в SOUZ")}
                                            </DevForm.Button>{" "}
                                        </>
                                    )}

                                    {!mainform?.sezam_client_secret && (
                                        <>
                                            <DevForm.Button variant="secondary" name="create_sezam" onClick={doAction}>
                                                {t("Создать ID в SEZAM")}
                                            </DevForm.Button>{" "}
                                        </>
                                    )}

                                    {(!mainform?.my_tracker_app_id || !mainform?.my_tracker_project_id) && (
                                        <DevForm.Button variant="secondary" name="create_mytracker" onClick={doAction}>
                                            {t("Создать ID приложения в myTracker")}
                                        </DevForm.Button>
                                    )}

                                    {mainform?.my_tracker_project_id && (
                                        <DevForm.Button variant="secondary" name="update_mytracker" onClick={doAction}>
                                            {t("Обновить проект в myTracker")}
                                        </DevForm.Button>
                                    )}
                                </div>
                            </Tab>
                            <Tab eventKey="gc" title={t("ИЦ")}>
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_type_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_distrib_id" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_storage" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_server_type" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_package_mask" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_package_name" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_installed_name" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_installed_name_auto" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_installed_file_names" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_gup_url_base" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_check_ban" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_check_ban_secret" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_check_ban_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_pers_list_url_kind" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_pers_list_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_accept_login" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_autoreg" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_registration_webform_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_license_text" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_shards_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_cbt_registration_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_game_page_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_user_profile_path" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_after_delete_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_mrac_url" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_mrac_secret" />
                                <DevForm.Field {...fieldProps} prefix="main" name="gc_flags" />

                                <DevForm.Button variant="primary" type="submit" name="save">
                                    {t("Сохранить")}
                                </DevForm.Button>
                            </Tab>
                            <Tab eventKey="hl" title={t("Скриншоты и видео")}>
                                <Highlights {...props} />
                            </Tab>
                            <Tab eventKey="sys_reqs" title={t("Системные требования")}>
                                <fieldset>
                                    <h6>{t("Минимальные системные требования")}</h6>
                                    <RenderForm prefix={`sys_req_min_form_${curLoc}`} bareForm={true} />
                                </fieldset>
                                <hr />
                                <fieldset>
                                    <h6>{t("Рекомендуемые системные требования")}</h6>
                                    <RenderForm prefix={`sys_req_rec_form_${curLoc}`} bareForm={true} />
                                </fieldset>
                                <DevForm.Button variant="primary" type="submit" name="save">
                                    {t("Сохранить")}
                                </DevForm.Button>
                            </Tab>
                            <Tab eventKey="social_links" title={t("Соцсети")}>
                                <div className="dmg-callout dmg-callout-info mb-3">
                                    {t(
                                        "Добавьте ссылки на сообщества проекта. После модерации они отобразятся на библиотечной странице игры блоком кликабильных иконок."
                                    )}
                                </div>
                                <RenderForm prefix={`social_form_${curLoc}`} bareForm={true} />
                                <DevForm.Button
                                    variant="primary"
                                    type="submit"
                                    name="save"
                                    style={{ marginTop: "10px" }}
                                >
                                    {t("Сохранить")}
                                </DevForm.Button>
                            </Tab>
                            <Tab eventKey="countries" title={t("Страны")}>
                                <Countries {...props} />
                                <DevForm.Button
                                    style={{ marginTop: "10px" }}
                                    variant="primary"
                                    type="submit"
                                    name="save"
                                >
                                    {t("Сохранить")}
                                </DevForm.Button>
                            </Tab>
                            <Tab eventKey="revenue_share" title={t("Доля с продаж")}>
                                <Alert variant="info">
                                    <Trans>
                                        Чтобы добавить новые значения для "Доля с продаж, %", необходимо согласовать
                                        действия с руководителем и создать задачу в Jira на компонент "Кабинет
                                        разработчика" с указанием даты, с которой данные значения должны начать
                                        применяться.
                                    </Trans>
                                </Alert>
                                <DevForm.Field {...fieldProps} prefix="main" name="revenue_share" />
                                {apiData.revenue_share_history.length > 0 && (
                                    <Table hover responsive>
                                        <thead>
                                            <tr>
                                                <th>{t("Доля с продаж")}</th>
                                                <th>{t("Дата изменения")}</th>
                                                <th>{t("Обоснование")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {apiData.revenue_share_history.map((o) => {
                                                return (
                                                    <tr key={`revenue_share_history${o.id}`}>
                                                        <td>{o.revenue_share}</td>
                                                        <td>{o.created.split(" ")[0]}</td>
                                                        <td className="show-wrap-text">{o.comment}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                )}
                            </Tab>
                            <Tab eventKey="gc_misc_params" title={t("Блоки на странице игры")}>
                                <GcMiscParams apiData={apiData} setApiData={setApiData} />
                                <DevForm.Button variant="primary" type="submit" name="save">
                                    {t("Сохранить")}
                                </DevForm.Button>
                            </Tab>
                        </Tabs>
                    </Form>
                </>
            )}
        </CommonCard>
    );
}

const SrtItem = ({ id, el, type, ind, toggleBlock, delBlock, changeField, types }) => {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id,
        data: {
            collection: type,
            type: id,
        },
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
    };

    return (
        <li className="py-1" ref={setNodeRef} style={style}>
            <Card style={{ backgroundColor: "#26a1d024" }}>
                <Card.Header>
                    <div className="gcmp-card-header">
                        <div className="mr-2" {...listeners} {...attributes}>
                            <Awesome type="bars" />
                        </div>
                        <a href="#" onClick={(ev) => toggleBlock(ev, type, ind)}>
                            {el.type ? types[type][el.type] : el.title || "---"}
                        </a>
                        <div className="gcmp-card-header-close">
                            <a href="#" onClick={(ev) => delBlock(ev, type, ind)}>
                                <Awesome type="close" />
                            </a>
                        </div>
                    </div>
                </Card.Header>
                {el.__show && (
                    <Card.Body>
                        {!el.type && (
                            <FormFieldGroup label={t("Название")} asRow={false}>
                                <DevForm.TextInput
                                    name="title"
                                    value={el.title}
                                    onChange={(ev) => changeField(ev, type, ind)}
                                />
                            </FormFieldGroup>
                        )}
                        <FormFieldGroup label={t("Тип")} asRow={false}>
                            <DevForm.Select
                                choices={types[type]}
                                value={el.type}
                                onChange={(data) => {
                                    const ev = {
                                        target: {
                                            name: "type",
                                            value: (data && data.value) || "",
                                        },
                                    };
                                    changeField(ev, type, ind);
                                }}
                            />
                        </FormFieldGroup>
                        {!el.type && (
                            <>
                                <FormFieldGroup label={t("Высота")} asRow={false}>
                                    <DevForm.TextInput
                                        name="height"
                                        value={el.height}
                                        onChange={(ev) => changeField(ev, type, ind)}
                                    />
                                </FormFieldGroup>
                                <FormFieldGroup label={t("Ссылка")} asRow={false}>
                                    <DevForm.TextInput
                                        name="url"
                                        value={el.url}
                                        onChange={(ev) => changeField(ev, type, ind)}
                                    />
                                </FormFieldGroup>
                            </>
                        )}
                    </Card.Body>
                )}
            </Card>
        </li>
    );
};

const SrtList = ({ items, type, toggleBlock, delBlock, changeField, types, onSortEnd }) => {
    const sensors = useSensors(useSensor(PointerSensor));
    const itemIds = useMemo(() => items.map((item) => item.type), [items]);

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSortEnd}>
            <ul className="li-wo-dots">
                <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                    {items.map((item, index) => (
                        <SrtItem
                            id={item.type}
                            key={`srt_${item.type}_${index}`}
                            collection={type}
                            el={item}
                            type={type}
                            ind={index}
                            toggleBlock={toggleBlock}
                            delBlock={delBlock}
                            changeField={changeField}
                            types={types}
                        />
                    ))}
                </SortableContext>
            </ul>
        </DndContext>
    );
};

function GcMiscParams({ apiData, setApiData }) {
    // TODO: сделать рефакторинг
    const { t } = useTranslation();
    const [gcmp, setGCMP] = useState({});

    const types = {
        custom_iframes: {
            "": "---------",
            serial_number: t("Серийный номер игры"),
            about: t("Описание игры"),
            buy_gift: t("Купить в подарок"),
            attributes: t("Атрибуты"),
            marking: t("Маркировка"),
            community_button: t("Кнопка сообщества"),
            eula: "EULA",
            extinfo: t("Выходные данные"),
            friends: t("Друзья в игре"),
            profit: t("Преимущества покупки"),
            promo: t("Акция"),
            recommend: t("Рекомендуемое"),
            shares: t("Поделиться"),
            sysreqs: t("Системные требования"),
            same_publisher: t("Другие игры издателя"),
            early_access_game: t("Игра в раннем доступе"),
        },
        custom_wide_iframes: {
            "": "---------",
            about_text: t("Об игре"),
            activate_instruction: t("Инструкция по активации"),
            articles: t("Новости по игре"),
            community_news: t("Новости сообщества"),
            countdown: t("Доступна для покупки (обратный отсчет)"),
            cross_promo: t("Кросс-промо"),
            linked: t("Дополнения"),
            bundles: t("Наборы"),
            bundle_items: t("Состав набора"),
            highlights: t("Скриншоты"),
            demo: t("Демо"),
            microreviews: t("Отзывы"),
            pc_rating: t("Оценка редакции"),
            secrets: t("Секреты"),
            igromarket: t("Игромаркет"),
        },
    };
    types.custom_iframes_in_library = types.custom_iframes;
    types.custom_wide_iframes_in_library = types.custom_wide_iframes;

    useEffect(() => {
        if (apiData.forms_data) {
            setGCMP(JSON.parse(apiData.forms_data.main.data.gc_misc_params));
        }
    }, []);

    useEffect(() => {
        if (apiData.forms_data) {
            const newApiData = { ...apiData };
            newApiData.forms_data.main.data.gc_misc_params = structuredClone(gcmp);
            Object.keys(types).forEach((tp) => {
                if (tp in newApiData.forms_data.main.data.gc_misc_params) {
                    newApiData.forms_data.main.data.gc_misc_params[tp].forEach((v) => {
                        delete v.__show;
                    });
                }
            });
            newApiData.forms_data.main.data.gc_misc_params = JSON.stringify(
                newApiData.forms_data.main.data.gc_misc_params
            );
            setApiData(newApiData);
        }
    }, [JSON.stringify(gcmp)]);

    const addBlock = (type) => {
        const newGCMP = { ...gcmp };
        if (!(type in newGCMP)) {
            newGCMP[type] = [];
        }
        newGCMP[type].unshift({ __show: true });
        setGCMP(newGCMP);
    };
    const toggleBlock = (ev, type, ind) => {
        ev.preventDefault();
        const newGCMP = { ...gcmp };
        newGCMP[type][ind].__show = !newGCMP[type][ind].__show;
        setGCMP(newGCMP);
    };
    const delBlock = (ev, type, ind) => {
        ev.preventDefault();
        const newGCMP = { ...gcmp };
        newGCMP[type].splice(ind, 1);
        setGCMP(newGCMP);
    };
    const changeField = (ev, type, ind) => {
        const { name } = ev.target;
        const value = ev.target.type === "checkbox" ? (ev.target.checked ? "1" : "0") : ev.target.value;
        const newGCMP = { ...gcmp };
        if (ind !== undefined) {
            newGCMP[type][ind][name] = value;
            newGCMP[type][ind] = cleanDefaultField(newGCMP[type][ind]);
        } else if (type === undefined) {
            newGCMP[name] = value;
        } else {
            if (!(type in newGCMP)) {
                newGCMP[type] = {};
            }
            newGCMP[type][name] = value;
        }
        setGCMP(newGCMP);
    };

    function cleanDefaultField(obj) {
        return obj.type ? { type: obj.type } : obj;
    }

    const onSortEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const newGCMP = { ...gcmp };
            const { collection } = active.data.current;
            const oldIndex = gcmp[collection].findIndex((item) => item.type === active.data.current.type);
            const newIndex = gcmp[collection].findIndex((item) => item.type === over.data.current.type);
            newGCMP[collection] = arrayMove(gcmp[collection], oldIndex, newIndex);
            setGCMP(newGCMP);
        }
    };

    return (
        <>
            <div className="gcmp-block">
                <h5>{t("Игра не в библиотеке")}</h5>
                <Row className="mb-3">
                    <Col md={12}>
                        <Card style={{ backgroundColor: "#26a1d024" }}>
                            <Card.Header>
                                <DevForm.Checkbox
                                    label={t("Верхний баннер во всю ширину страницы")}
                                    name="enabled"
                                    checked={gcmp.top_wide_frame?.enabled === "1"}
                                    onChange={(ev) => changeField(ev, "top_wide_frame")}
                                />
                            </Card.Header>
                            {gcmp.top_wide_frame?.enabled === "1" && (
                                <Card.Body>
                                    <FormFieldGroup label={t("Высота")}>
                                        <DevForm.TextInput
                                            name="height"
                                            value={gcmp.top_wide_frame?.height}
                                            onChange={(ev) => changeField(ev, "top_wide_frame")}
                                        />
                                    </FormFieldGroup>
                                    <FormFieldGroup label={t("Ссылка")}>
                                        <DevForm.TextInput
                                            name="url"
                                            value={gcmp.top_wide_frame?.url}
                                            onChange={(ev) => changeField(ev, "top_wide_frame")}
                                        />
                                    </FormFieldGroup>
                                </Card.Body>
                            )}
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col md={7}>
                        <Button
                            variant="primary"
                            size="sm"
                            className="float-right"
                            onClick={() => addBlock("custom_wide_iframes")}
                        >
                            {t("Добавить")}
                        </Button>
                    </Col>
                    <Col md={5}>
                        <Button
                            variant="primary"
                            size="sm"
                            className="float-right"
                            onClick={() => addBlock("custom_iframes")}
                        >
                            {t("Добавить")}
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col md={7}>
                        <SrtList
                            type="custom_wide_iframes"
                            items={gcmp.custom_wide_iframes || []}
                            toggleBlock={toggleBlock}
                            delBlock={delBlock}
                            changeField={changeField}
                            types={types}
                            onSortEnd={onSortEnd}
                        />
                    </Col>
                    <Col md={5}>
                        <SrtList
                            type="custom_iframes"
                            items={gcmp.custom_iframes || []}
                            toggleBlock={toggleBlock}
                            delBlock={delBlock}
                            changeField={changeField}
                            types={types}
                            onSortEnd={onSortEnd}
                        />
                    </Col>
                </Row>
                <h5>{t("Игра в библиотеке")}</h5>
                <Row className="mb-3">
                    <Col md={12}>
                        <Card style={{ backgroundColor: "#26a1d024" }}>
                            <Card.Header>
                                <DevForm.Checkbox
                                    label={t("Верхний баннер во всю ширину страницы")}
                                    name="enabled"
                                    checked={gcmp.top_wide_frame_in_library?.enabled === "1"}
                                    onChange={(ev) => changeField(ev, "top_wide_frame_in_library")}
                                />
                            </Card.Header>
                            {gcmp.top_wide_frame_in_library?.enabled === "1" && (
                                <Card.Body>
                                    <FormFieldGroup label={t("Высота")}>
                                        <Form.Control
                                            type="text"
                                            name="height"
                                            value={gcmp.top_wide_frame_in_library?.height || ""}
                                            onChange={(ev) => changeField(ev, "top_wide_frame_in_library")}
                                        />
                                    </FormFieldGroup>
                                    <FormFieldGroup label={t("Ссылка")}>
                                        <Form.Control
                                            type="text"
                                            name="url"
                                            value={gcmp.top_wide_frame_in_library?.url || ""}
                                            onChange={(ev) => changeField(ev, "top_wide_frame_in_library")}
                                        />
                                    </FormFieldGroup>
                                </Card.Body>
                            )}
                        </Card>
                    </Col>
                </Row>
                <Row>
                    <Col md={7}>
                        <Button
                            variant="primary"
                            size="sm"
                            className="float-right"
                            onClick={() => addBlock("custom_wide_iframes_in_library")}
                        >
                            {t("Добавить")}
                        </Button>
                    </Col>
                    <Col md={5}>
                        <Button
                            variant="primary"
                            size="sm"
                            className="float-right"
                            onClick={() => addBlock("custom_iframes_in_library")}
                        >
                            {t("Добавить")}
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col md={7}>
                        <SrtList
                            type="custom_wide_iframes_in_library"
                            items={gcmp.custom_wide_iframes_in_library || []}
                            toggleBlock={toggleBlock}
                            delBlock={delBlock}
                            changeField={changeField}
                            types={types}
                            onSortEnd={onSortEnd}
                        />
                    </Col>
                    <Col md={5}>
                        <SrtList
                            type="custom_iframes_in_library"
                            items={gcmp.custom_iframes_in_library || []}
                            toggleBlock={toggleBlock}
                            delBlock={delBlock}
                            changeField={changeField}
                            types={types}
                            onSortEnd={onSortEnd}
                        />
                    </Col>
                </Row>
            </div>

            <hr />
            <DevForm.Field apiData={apiData} setApiData={setApiData} prefix="main" name="gc_misc_params" />
        </>
    );
}

const ChangedFieldsModalAlert = ({ fields, showModal, handleClose, submitForm }) => {
    const { t } = useTranslation();
    const [data, setData] = useState({});
    const [typeField, setTypeField] = useState(false);
    const [developerField, setDeveloperField] = useState(false);
    const [m2mGameDevsField, setM2mGameDevsField] = useState(false);
    const [m2mPublishersField, setM2mPublishersField] = useState(false);
    const saveHandle = () => {
        submitForm();
        handleClose();
    };

    useEffect(() => {
        const tpField = Boolean(fields?.type);
        setTypeField(tpField);
        if (tpField) {
            delete fields.type;
        }

        const devField = Boolean(fields?.developer);
        setDeveloperField(devField);
        if (devField) {
            delete fields.developer;
        }

        const m2mGameDevs = Boolean(fields.m2m_gamedevs);
        setM2mGameDevsField(m2mGameDevs);
        if (m2mGameDevs) {
            delete fields.m2m_gamedevs;
        }

        const m2mPublishers = Boolean(fields.m2m_publishers);
        setM2mPublishersField(m2mPublishers);
        if (m2mPublishers) {
            delete fields.m2m_publishers;
        }

        setData(fields);
    }, [fields]);

    return (
        <DevModal
            show={showModal}
            onHide={handleClose}
            header={t("Внимание!")}
            body={
                <>
                    {!isEmptyObject(data) && (
                        <>
                            <p>{t("Вы собираетесь изменить поля, связанные с маркетинговыми настройками игры:")}</p>
                            <ul>
                                {Object.keys(fields).map((field) => (
                                    <li key={field}>{fields[field].label}</li>
                                ))}
                            </ul>
                            <p>
                                <Trans>
                                    Эти поля управляются командой маркетинга и связаны с настройками, получаемыми от
                                    сервиса myTracker. Изменение этих данных без согласования с командой маркетинга
                                    может повлиять на корректность аналитики и маркетинговой стратегии.
                                </Trans>
                            </p>
                            <b>{t("Пожалуйста, согласуйте изменения с командой маркетинга перед сохранением.")}</b>
                        </>
                    )}

                    {!isEmptyObject(data) && typeField && <hr />}

                    {typeField && (
                        <Trans i18nKey="trans_admin_change_subtype">
                            <p>
                                Вы собираетесь изменить поле <b>«Подтип игры»</b>.
                            </p>
                            <p>
                                Перед применением данной настройки убедитесь, что выполнены все необходимые
                                подготовительные работы.
                            </p>
                            <p>
                                Если вы переключаете игру с <b>Free-to-play</b> на <b>Premium</b>, сначала свяжитесь с
                                командой кабинета разработчика, чтобы согласовать начисление бесплатного заказа всем
                                зарегистрированным пользователям. Это необходимо, чтобы игроки не видели кнопку «Купить»
                                и могли продолжать играть.
                            </p>
                            <p>
                                Если вы переключаете игру с <b>Premium</b> на <b>Free-to-play</b>, дополнительных
                                подготовительных действий не требуется. Обратите внимание, что переключение повлечёт за
                                собой обнуление цен и удаление акционных кампаний в разделе <b>«Цены и скидки»</b>.
                            </p>
                            <p>
                                <b>Важно!</b> После подтверждения новой настройки необходимо проверить следующие поля:
                            </p>
                            <ul>
                                <li>Подтип игры</li>
                                <li>Атрибуты</li>
                                <li>Цена (RUB)</li>
                                <li>Цена (EUR)</li>
                                <li>Цена (USD)</li>
                                <li>Постоянная скидка</li>
                                <li>Платежи остановлены</li>
                                <li>Набор ЕдОк merchants</li>
                            </ul>
                        </Trans>
                    )}

                    {!isEmptyObject(data) && developerField && <hr />}

                    {developerField && (
                        <Trans i18nKey="trans_admin_change_developer">
                            <p>Вы собираетесь изменить поле Разработчик.</p>
                            <p>Изменение разработчика влияет на маркетинговые и финансовые настройки игры:</p>
                            <p>
                                1. Маркетинговые настройки: Настройки myTracker привязаны к конкретному разработчику.
                                Перед сохранением изменений необходимо получить согласие от команды маркетинга и
                                запросить перенастройку myTracker для этой игры.
                            </p>
                            <p>
                                2. Финансовые настройки: При изменении разработчика может потребоваться обновление доли
                                с продаж, поэтому данное изменение должно быть согласовано с командой кабинета
                                разработчика для своевременного обновления финансовых параметров игры.
                            </p>
                            <p>
                                <b>Пожалуйста, согласуйте изменения с соответствующими командами перед сохранением.</b>
                            </p>
                        </Trans>
                    )}

                    {!isEmptyObject(data) && m2mGameDevsField && <hr />}

                    {m2mGameDevsField && (
                        <p>
                            <Trans>
                                В поле <b>Разработчики</b> содержится больше значений, чем позволяет лимит. Пожалуйста,
                                проверьте введенные данные перед сохранением.
                            </Trans>
                        </p>
                    )}

                    {!isEmptyObject(data) && m2mPublishersField && <hr />}

                    {m2mPublishersField && (
                        <p>
                            <Trans>
                                В поле <b>Издатели</b> содержится больше значений, чем позволяет лимит. Пожалуйста,
                                проверьте введенные данные перед сохранением.
                            </Trans>
                        </p>
                    )}
                </>
            }
            footer={
                <>
                    <Button variant="primary" onClick={saveHandle}>
                        {t("Сохранить")}
                    </Button>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("Отмена")}
                    </Button>
                </>
            }
        />
    );
};
